/**
 * Протокол WebSocket.
 *
 * Устройство комнат — часть модели безопасности, а не транспортная деталь.
 * Подписка на комнату требует проверки прав ровно так же, как HTTP-запрос
 * (§15 ТЗ).
 */

import type { AnyTelegramAccountDto, TelegramChatDto, TelegramMessageDto } from './dto.js';
import type { TelegramChatAction } from './telegram-gateway.js';

/**
 * Идентификатор комнаты. Строго типизирован, чтобы нельзя было подписаться на
 * произвольную строку и чтобы парсинг scope был однозначным.
 */
export type RoomId =
  | `user:${string}`
  | `telegram-account:${string}`
  | `chat:${string}`;

export function accountRoom(accountId: string): RoomId {
  return `telegram-account:${accountId}`;
}

export function chatRoom(chatId: string): RoomId {
  return `chat:${chatId}`;
}

export function userRoom(userId: string): RoomId {
  return `user:${userId}`;
}

export interface ParsedRoom {
  kind: 'user' | 'telegram-account' | 'chat';
  id: string;
}

/**
 * Разбор комнаты. Возвращает null на любом неизвестном формате — движок
 * подписки обязан отказать, а не пытаться угадать намерение клиента.
 */
export function parseRoom(room: string): ParsedRoom | null {
  const sep = room.indexOf(':');
  if (sep <= 0) return null;

  const kind = room.slice(0, sep);
  const id = room.slice(sep + 1);
  if (id.length === 0) return null;

  if (kind === 'user' || kind === 'telegram-account' || kind === 'chat') {
    return { kind, id };
  }
  return null;
}

// ── Клиент → сервер ──

export type ClientMessage =
  | { type: 'subscribe'; room: RoomId }
  | { type: 'unsubscribe'; room: RoomId }
  | { type: 'presence'; chatId: string; state: 'viewing' | 'replying' | 'idle' }
  | { type: 'ping' };

// ── Сервер → клиент ──

export type ServerMessage =
  | { type: 'subscribed'; room: RoomId }
  | { type: 'unsubscribed'; room: RoomId }
  | { type: 'error'; code: 'FORBIDDEN' | 'BAD_REQUEST' | 'RATE_LIMITED'; room?: string }
  /**
   * Принудительный выход из комнаты после отзыва доступа. Без него менеджер
   * продолжал бы получать сообщения по отобранному аккаунту до конца сессии.
   */
  | { type: 'revoked'; room: RoomId }
  | { type: 'account-state'; account: AnyTelegramAccountDto }
  | { type: 'chat-updated'; chat: TelegramChatDto }
  | { type: 'message-created'; chatId: string; message: TelegramMessageDto }
  | { type: 'message-updated'; chatId: string; message: TelegramMessageDto }
  /** Собеседник прочитал наши сообщения до указанного включительно. */
  | { type: 'chat-read-outbox'; chatId: string; lastReadOutboxMessageId: string }
  /**
   * Собеседник печатает / записывает / отправляет. Гаснет само через
   * несколько секунд, если не пришло новое; `cancel` гасит сразу.
   */
  | {
      type: 'chat-action';
      chatId: string;
      senderName: string | null;
      action: TelegramChatAction;
    }
  /**
   * Внутреннее присутствие. В Telegram НЕ транслируется никогда (§30 ТЗ):
   * это событие нашей системы, а не sendChatAction.
   */
  | {
      type: 'presence';
      chatId: string;
      userId: string;
      displayName: string;
      state: 'viewing' | 'replying' | 'idle';
    }
  | { type: 'pong' };
